import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { DenunciaCategoria, SubcategoriaDiscriminacao } from '@aions/core';
import { LGP_02_VERSION } from '@aions/core';
import {
  deriveKeyMaterial,
  splitKeyMaterial,
  generateDek,
  encryptPayload,
  wrapDek,
  wrapDekWithEscrow,
} from '@aions/crypto';
import { Step1Categoria } from '../components/wizard/Step1Categoria';
import { Step2Fato } from '../components/wizard/Step2Fato';
import { Step3Anexos } from '../components/wizard/Step3Anexos';
import { Step4Revisao } from '../components/wizard/Step4Revisao';
import { Step5Confirmacao, type AnexosOutcome } from '../components/wizard/Step5Confirmacao';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { generateCaseId } from '../utils/case-id';
import { bytesToBase64 } from '../utils/bytes';
import { getEscrowPublicJwk } from '../config/escrow';
import { saveDraft, loadDraft, clearDraft } from '../lib/draft-storage';
import { uploadAttachment } from '../lib/attachments-upload';
import { apiUrl } from '../lib/api-base';
import { fetchAndWrapForMembers } from '../lib/member-wraps';
import { buildIdentidadeBlock } from '../lib/identidade-block';
import { identidadeIncompleta } from '../lib/identidade-form';

export interface WizardData {
  categoria: DenunciaCategoria | null;
  /** Trilha 2a — sub-tipos opcionais; só preenchido quando
   *  categoria ∈ {discriminacao, discriminacao_salarial_genero}. */
  subcategoriasDiscriminacao: ReadonlyArray<SubcategoriaDiscriminacao>;
  descricao: string;
  quando: string;
  onde: string;
  envolvidos: string;
  files: File[];
  identificacaoVoluntaria: boolean;
  /** P10 (ADR-0018) — identificação VOLUNTÁRIA. Só usados se identificacaoVoluntaria.
   *  Cifrados client-side num BLOCO SEPARADO sob a DEK (a IA nunca vê). CPF opcional. */
  nome: string;
  cpf: string;
  contato: string;
  /** Sprint 3.1 — aceite obrigatório LGP-02 no Step4 antes do submit. */
  policyAccepted: boolean;
}

interface IntakeWizardProps {
  onComplete?: () => void;
}

const TOTAL_STEPS = 5;

export function IntakeWizard({ onComplete }: IntakeWizardProps): JSX.Element {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  // D-29 — token do Turnstile capturado no Step 4. Sem token o submit fica
  // bloqueado. Em dev a test sitekey resolve sozinha; em prod vem do widget real.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [result, setResult] = useState<{
    protocolo: string;
    passphrase: string;
    caseId: string;
  } | null>(null);
  // Desfecho dos anexos, que sobem DEPOIS de esta tela estar no ar (ver handleSubmit).
  const [anexos, setAnexos] = useState<AnexosOutcome | null>(null);
  const [data, setData] = useState<WizardData>(() => {
    // Restore from localStorage if a draft exists (D-25, INT-07).
    const draft = loadDraft();
    if (draft) return draft;
    return {
      categoria: null,
      subcategoriasDiscriminacao: [],
      descricao: '',
      quando: '',
      onde: '',
      envolvidos: '',
      files: [],
      identificacaoVoluntaria: false,
      nome: '',
      cpf: '',
      contato: '',
      policyAccepted: false,
    };
  });

  const updateData = useCallback((partial: Partial<WizardData>) => {
    setData((prev) => ({ ...prev, ...partial }));
  }, []);

  /**
   * P10 — liga/desliga a identificação voluntária.
   *
   * Desligar APAGA nome/cpf/contato. Sem isso o dado fica pendurado no estado
   * depois da pessoa desistir, e re-marcar a caixa ressuscita exatamente o que
   * ela acabou de recusar. As duas portas de saída (o checkbox e o botão
   * "Prefiro continuar anônimo") passam por AQUI — uma cópia só da limpeza.
   *
   * O bloco cifrado já era gated pelo booleano (buildIdentidadeBlock), então
   * isso não muda um byte do POST; muda o que a máquina segura em memória
   * depois de um "não".
   */
  const handleToggleIdentificacao = useCallback(
    (v: boolean) => {
      updateData(
        v
          ? { identificacaoVoluntaria: true }
          : { identificacaoVoluntaria: false, nome: '', cpf: '', contato: '' },
      );
    },
    [updateData],
  );

  // Debounced auto-save to localStorage. Files are dropped by saveDraft.
  // Skips persistence after submit (step 5) — the draft is already cleared then.
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (step === 5) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveDraft(data);
    }, 500);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [data, step]);

  const canAdvance = (): boolean => {
    if (step === 1) return data.categoria !== null;
    if (step === 2) return data.descricao.length >= 50;
    return true;
  };

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setAnexos(null);

    // Hold raw DEK in scope so we can zeroize after wraps (P1-B residual mitigation).
    let dekRaw: Uint8Array | null = null;
    // Trava do catch: uma vez exibidas as credenciais, nenhum erro posterior pode
    // transformar a tela em "erro ao enviar". Local (não state) porque o catch roda
    // no mesmo escopo e leria um state defasado.
    let credenciaisExibidas = false;

    try {
      // 1. Generate credentials + caseId client-side.
      //    caseId is required BEFORE encrypt because AAD = caseId (D-23, CRY-04).
      const protocolo = generateProtocolo();
      const passphrase = generatePassphrase();
      const caseId = generateCaseId();

      // 2. Derive 64 bytes via Argon2id (D-21, ANON-03).
      //    Server only ever sees authHash[0..32]; kekRaw[32..64] stays in browser.
      const material = await deriveKeyMaterial(protocolo, passphrase);
      const { authHash, kekRaw } = splitKeyMaterial(material);

      // 3. Generate per-denúncia DEK (D-22, CRY-01).
      dekRaw = generateDek();

      // 4. Encrypt payload with AAD = caseId (D-23 cross-case-swap protection).
      //    policyAccepted + policyVersion no payload cifrado servem como prova
      //    forense indelével do aceite no momento da denúncia (Sprint 3.1).
      const policyAcceptedAt = Date.now();
      const plaintext = JSON.stringify({
        descricao: data.descricao,
        quando: data.quando,
        onde: data.onde,
        envolvidos: data.envolvidos,
        identificacaoVoluntaria: data.identificacaoVoluntaria,
        policyAccepted: true,
        policyVersion: LGP_02_VERSION,
        policyAcceptedAt,
      });
      const { ciphertext, nonce } = await encryptPayload(plaintext, dekRaw, caseId);

      // P10 (ADR-0018) — bloco cifrado SEPARADO da identificação voluntária (só com
      // opt-in; sem opt-in → objeto vazio → POST byte-idêntico ao anônimo). Extraído
      // para `buildIdentidadeBlock` (testável): o invariante "a IA nunca vê a
      // identidade" é estrutural — o bloco fica FORA do ciphertext que a Analista
      // decifra. Ver lib/identidade-block.ts + identidade-block.test.ts.
      const identidadeBlock = await buildIdentidadeBlock(data, dekRaw, caseId);

      // 5. Wrap DEK twice — by KEK_protocolo (AES-KW) and KEK_escrow (RSA-OAEP).
      const escrowPublicJwk = await getEscrowPublicJwk();
      const dekWrappedByProtocolo = await wrapDek(dekRaw, kekRaw);
      const dekWrappedByEscrow = await wrapDekWithEscrow(dekRaw, escrowPublicJwk);

      // ADR-0011 Fatia 2 Wave 3 — embrulha o DEK pras públicas do comitê (NÃO-FATAL).
      // fetchAndWrapForMembers SEMPRE resolve: falha/timeout/flag-OFF ([]) → denúncia
      // segue sem esses wraps (= comportamento de hoje). O DEK não sai em claro — só
      // os wraps RSA-OAEP. Custo desprezível ante o Argon2id já awaited acima; sem
      // spinner novo (cai no estado de "enviando" existente) e timeout de 4s blinda
      // contra pendurar o submit.
      const dekWrappedByMembers = await fetchAndWrapForMembers(dekRaw);

      // 6. Submit denúncia first — attachments need a registered caseId on the
      //    server before /sign-url accepts them (attachments.ts looks up by caseId).
      //    policyAcceptedAt + policyVersion vão no body cleartext para o server
      //    validar (paridade Sprint 3.1 middleware) — NÃO identifica denunciante,
      //    apenas atesta o aceite jurídico.
      const resp = await fetch(apiUrl('/api/denuncia'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          caseId,
          authHash: bytesToBase64(authHash),
          ciphertext: bytesToBase64(ciphertext),
          nonce: bytesToBase64(nonce),
          dekWrappedByProtocolo: bytesToBase64(dekWrappedByProtocolo),
          dekWrappedByEscrow: bytesToBase64(dekWrappedByEscrow),
          // ADR-0011 Fatia 2 — só vai se houver wrap são (vazio/flag-OFF → omitido,
          // POST idêntico ao de hoje). Já é base64 (a lib converteu).
          ...(dekWrappedByMembers.length > 0 ? { dekWrappedByMembers } : {}),
          // P10 (ADR-0018) — bloco cifrado da identidade (só com opt-in). Sem
          // opt-in → objeto vazio → POST byte-idêntico ao anônimo de hoje.
          ...identidadeBlock,
          categoria: data.categoria,
          // Trilha 2a — sub-tipos opcionais (multi-select). Só envia quando
          // categoria ∈ {discriminacao, discriminacao_salarial_genero} e o
          // denunciante marcou ao menos um. Array vazio omitido por paridade
          // com o mapping do SupabaseRepo (null no DB = não preencheu).
          ...(data.subcategoriasDiscriminacao.length > 0
            ? { subcategoriaDiscriminacao: data.subcategoriasDiscriminacao }
            : {}),
          nonceSubmission: crypto.randomUUID(),
          timestampSubmission: Date.now(),
          // Token real do widget Turnstile (D-29). Validado server-side pelo
          // turnstileGuard com siteverify; em dev o worker usa TEST_BYPASS.
          turnstileToken,
          policyAccepted: true,
          policyVersion: LGP_02_VERSION,
          policyAcceptedAt,
        }),
      });

      if (!resp.ok) {
        // 🐞 QA-front #5 — 429 (rate-limit ligado hoje) ganha mensagem própria;
        // demais falhas caem no genérico. Mantém alert() (inline fica p/ #6).
        alert(resp.status === 429 ? t('wizard.submit_rate_limited') : t('wizard.submit_error'));
        return;
      }

      // 7. A denúncia ESTÁ GRAVADA. As credenciais entram na tela AGORA — nada
      //    depois desta linha pode escondê-las.
      //
      //    ⚠️ Ordem invertida em 2026-08-04, e a inversão é o ponto: os uploads de
      //    anexo rodavam AQUI, antes do setResult. `protocolo` e `passphrase` nascem
      //    nesta função e nunca vão ao servidor (só o authHash derivado vai), então
      //    qualquer throw no meio dos anexos caía no catch e levava embora a ÚNICA
      //    cópia que existe delas — deixando no banco um caso que o comitê lê e que o
      //    denunciante nunca poderia acompanhar, porque sem protocolo não há lookup.
      //    O comentário antigo dizia que a falha de um anexo "aborta a submissão":
      //    não abortava nada, o registro já estava persistido. Era perda total de
      //    acesso disfarçada de erro de rede — e o 403 do /sign-url (o guard de
      //    Turnstile que vivia naquela rota) fazia isso acontecer em 100% das
      //    denúncias com anexo.
      clearDraft(); // D-25 / T-DRAFT_LEAK — só depois do submit aceito.
      setResult({ protocolo, passphrase, caseId });
      setStep(5);
      credenciaisExibidas = true;

      // 8. Anexos — cada arquivo é uma operação INDEPENDENTE: strip EXIF + encrypt
      //    com a mesma DEK + AAD=caseId. Falha de um não interrompe os outros nem
      //    derruba a tela; vira aviso NOMEADO no Step5. Perda visível, não filtrada
      //    em silêncio. O AttachmentRef é registrado pelo servidor no próprio
      //    PUT /upload, então o cliente não tem nada para devolver aqui.
      if (data.files.length > 0) {
        setAnexos({ fase: 'enviando', total: data.files.length, falhos: [] });
        const falhos: string[] = [];
        for (const f of data.files) {
          try {
            await uploadAttachment(f, dekRaw, caseId);
          } catch (err) {
            console.error('Attachment upload failed:', f.name, err);
            falhos.push(f.name);
          }
        }
        setAnexos({ fase: 'concluido', total: data.files.length, falhos });
      }
    } catch (err) {
      console.error('Submission error:', err);
      // Só alerta se as credenciais AINDA não estão na tela. Depois do passo 5 um
      // erro inesperado não pode virar "erro ao enviar" — a denúncia foi enviada.
      if (!credenciaisExibidas) alert(t('wizard.submit_error'));
    } finally {
      // Best-effort zeroize DEK plaintext in JS heap.
      if (dekRaw) dekRaw.fill(0);
      setSubmitting(false);
    }
  }, [data, turnstileToken]);

  return (
    <div className="fade-in">
      {/* Progress bar */}
      <div
        className="wizard-stepper"
        role="progressbar"
        aria-valuenow={step}
        aria-valuemin={1}
        aria-valuemax={TOTAL_STEPS}
      >
        {Array.from({ length: TOTAL_STEPS }, (_, i) => (
          <div
            key={i}
            className={`wizard-step-indicator ${i + 1 === step ? 'active' : ''} ${i + 1 < step ? 'completed' : ''}`}
          />
        ))}
      </div>

      <p className="wizard-step-label">
        {t('wizard.step_of', { current: String(step), total: String(TOTAL_STEPS) })}
      </p>

      <div className="card">
        {step === 1 && (
          <Step1Categoria
            selected={data.categoria}
            onSelect={(cat) => updateData({ categoria: cat })}
            subcategoriasDiscriminacao={data.subcategoriasDiscriminacao}
            onSubcategoriasChange={(next) => updateData({ subcategoriasDiscriminacao: next })}
          />
        )}
        {step === 2 && (
          <Step2Fato
            descricao={data.descricao}
            quando={data.quando}
            onde={data.onde}
            envolvidos={data.envolvidos}
            onChange={updateData}
          />
        )}
        {step === 3 && (
          <Step3Anexos files={data.files} onFilesChange={(files) => updateData({ files })} />
        )}
        {step === 4 && (
          <Step4Revisao
            data={data}
            onToggleIdentificacao={handleToggleIdentificacao}
            onIdentidadeChange={updateData}
            onTogglePolicy={(v) => updateData({ policyAccepted: v })}
          />
        )}
        {step === 5 && result && (
          <Step5Confirmacao
            protocolo={result.protocolo}
            passphrase={result.passphrase}
            caseId={result.caseId}
            onConsultar={onComplete}
            anexos={anexos ?? undefined}
          />
        )}

        {/* D-29 — Turnstile no Step 4, logo antes do envio. */}
        {step === 4 && (
          <TurnstileWidget onToken={setTurnstileToken} onExpire={() => setTurnstileToken(null)} />
        )}

        {/* Navigation */}
        {step < 5 && (
          <div className="wizard-actions">
            {step > 1 ? (
              <button className="btn btn-secondary" onClick={() => setStep((s) => s - 1)}>
                {t('wizard.back')}
              </button>
            ) : (
              <div />
            )}
            {step < 4 ? (
              <button
                className="btn btn-primary"
                disabled={!canAdvance()}
                onClick={() => setStep((s) => s + 1)}
              >
                {t('wizard.next')}
              </button>
            ) : step === 4 ? (
              <button
                className="btn btn-primary"
                disabled={
                  submitting ||
                  !data.policyAccepted ||
                  !turnstileToken ||
                  // P10 — se identificar, nome + contato são obrigatórios (CPF não).
                  // Regra em lib/identidade-form.ts, a MESMA que o Step4 usa para
                  // exibir o motivo do bloqueio. Duas leituras, uma fonte.
                  identidadeIncompleta(data)
                }
                onClick={handleSubmit}
                title={
                  !data.policyAccepted
                    ? t('step4.policy_required_hint')
                    : identidadeIncompleta(data)
                      ? t('step4.identidade_required_hint')
                      : !turnstileToken
                        ? t('turnstile.label')
                        : undefined
                }
              >
                {submitting ? <span className="spinner" /> : null}
                {t('wizard.submit')}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function generateProtocolo(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const parts: string[] = [];
  for (let g = 0; g < 3; g++) {
    let seg = '';
    for (let i = 0; i < 4; i++) {
      const arr = new Uint8Array(1);
      crypto.getRandomValues(arr);
      seg += chars[arr[0]! % chars.length];
    }
    parts.push(seg);
  }
  return parts.join('-');
}

function generatePassphrase(): string {
  // 6 random words from a curated list (sufficient entropy)
  const words = [
    'montanha',
    'floresta',
    'oceano',
    'vento',
    'estrela',
    'nuvem',
    'pedra',
    'chama',
    'trovao',
    'aurora',
    'lagoa',
    'colina',
    'brisa',
    'cristal',
    'deserto',
    'onda',
    'raio',
    'templo',
    'joia',
    'porto',
    'miragem',
    'cascata',
    'vulcao',
    'glaciar',
    'planalto',
    'ravina',
    'bosque',
    'gruta',
    'farol',
    'ancora',
    'planicie',
    'penhasco',
  ];
  const selected: string[] = [];
  for (let i = 0; i < 6; i++) {
    const arr = new Uint8Array(1);
    crypto.getRandomValues(arr);
    selected.push(words[arr[0]! % words.length]!);
  }
  return selected.join(' ');
}
